import {
  useGetIdentity,
  useGetList,
  useGetOne,
  useLocaleState,
  useNotify,
  useTranslate,
} from "ra-core";
import { ChevronRight } from "lucide-react";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { Confirm } from "@/components/admin/confirm";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Item,
  ItemActions,
  ItemContent,
  ItemGroup,
  ItemTitle,
} from "@/components/ui/item";

import { getSupabaseClient } from "../providers/supabase/supabase";
import type {
  CreditBalance,
  CreditMovement,
  CreditSide,
  CreditUsage,
  Sale,
} from "../types";
import { useCreditPrices } from "./creditPrices";
import { formatDay } from "./projectUtils";

/** Crédits : un solde commun par côté (équipe du client ou organisation partenaire) ; l'administrateur du côté lit
 * la consommation de chacun, le gestionnaire tous les côtés et ajoute ou retire des crédits. */
export const CreditList = () => {
  const translate = useTranslate();
  const { data: identity } = useGetIdentity();
  const { data: me } = useGetOne<Sale>(
    "sales",
    { id: identity?.id ?? "" },
    { enabled: !!identity },
  );
  const manager = !!me?.administrator && !!me?.subscription_manager;
  const {
    data: sides,
    isPending,
    refetch: refetchSides,
  } = useQuery({
    queryKey: ["credit_sides"],
    queryFn: async () => {
      const { data, error } = await getSupabaseClient().rpc("credit_sides");
      if (error) throw error;
      return (data ?? []) as CreditSide[];
    },
  });
  const [sideId, setSideId] = useState<number | null>(null);
  const side =
    sides?.find((candidate) => candidate.id === sideId) ?? sides?.[0];
  const months = lastMonths(6);
  const [month, setMonth] = useState(months[0]);
  const [person, setPerson] = useState<number | null>(null);
  const { data: usage, refetch: refetchUsage } = useQuery({
    queryKey: ["credit_usage", month, side?.id],
    enabled: !!side,
    queryFn: async () => {
      const { data, error } = await getSupabaseClient().rpc("credit_usage", {
        month,
        side: side?.id,
      });
      if (error) throw error;
      return (data ?? []) as CreditUsage[];
    },
  });
  const memberIds = (usage ?? []).map((row) => row.sales_id);
  const shown = person != null ? [person] : memberIds;
  const { data: movements, refetch: refetchMovements } =
    useGetList<CreditMovement>(
      "credit_ledger",
      {
        pagination: { page: 1, perPage: 50 },
        sort: { field: "created_at", order: "DESC" },
        filter: { "sales_id@in": `(${shown.join(",")})` },
      },
      { enabled: shown.length > 0 },
    );
  const prices = useCreditPrices();
  if (isPending || !identity) return null;
  const names = new Map(
    (usage ?? []).map((row) => [
      row.sales_id,
      `${row.first_name} ${row.last_name}`,
    ]),
  );
  const refetch = () => {
    refetchSides();
    refetchUsage();
    refetchMovements();
  };
  const sideName = (candidate: CreditSide) =>
    candidate.partner
      ? (candidate.name ?? "")
      : translate("resources.projects.credits.side_client");

  return (
    <div className="mt-2 pb-4 flex flex-col gap-4 max-w-4xl">
      <div>
        <h2 className="text-xl font-semibold">
          {translate("resources.projects.credits.title")}
        </h2>
        <p className="text-sm text-muted-foreground">
          {translate("resources.projects.credits.intro", {
            price: prices?.email ?? "…",
            mobile: prices?.mobile ?? "…",
            team: prices?.team ?? "…",
          })}
        </p>
      </div>
      {manager && (sides?.length ?? 0) > 1 && (
        <Select
          value={side ? String(side.id) : undefined}
          onValueChange={(value) => {
            setSideId(Number(value));
            setPerson(null);
          }}
        >
          <SelectTrigger
            className="w-full sm:w-80"
            aria-label={translate("resources.projects.credits.side_select")}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {sides?.map((candidate) => (
              <SelectItem key={candidate.id} value={String(candidate.id)}>
                {sideName(candidate)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      {side && (
        <SideCard
          side={side}
          name={sideName(side)}
          canGrant={manager}
          onGranted={refetch}
        />
      )}
      <Card>
        <CardContent className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-bold">
              {translate(
                `resources.projects.credits.${usage?.length === 1 && usage[0].sales_id === identity.id ? "usage_own" : "usage"}`,
              )}
            </h3>
            <Select
              value={month}
              onValueChange={(value) => {
                setMonth(value);
                setPerson(null);
              }}
            >
              <SelectTrigger
                className="w-44"
                aria-label={translate("resources.projects.credits.month")}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {months.map((value) => (
                  <SelectItem key={value} value={value}>
                    <MonthLabel month={value} />
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <UsageTable
            rows={usage ?? []}
            selected={person}
            onSelect={(id) => setPerson(id === person ? null : id)}
          />
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
            <h3 className="text-sm font-bold">
              {person != null
                ? translate("resources.projects.credits.movements_of", {
                    name: names.get(person) ?? "",
                  })
                : translate("resources.projects.credits.movements")}
            </h3>
            {person != null && (
              <Button size="sm" variant="ghost" onClick={() => setPerson(null)}>
                {translate("resources.projects.credits.all_movements")}
              </Button>
            )}
          </div>
          {!movements?.length || shown.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {translate("resources.projects.credits.no_movement")}
            </p>
          ) : (
            <ul className="flex flex-col gap-2">
              {movements.map((movement) => (
                <MovementRow
                  key={movement.id}
                  movement={movement}
                  name={
                    memberIds.length > 1
                      ? names.get(Number(movement.sales_id))
                      : undefined
                  }
                />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

/** Premiers jours des derniers mois, du plus récent au plus ancien (AAAA-MM-01). */
const lastMonths = (count: number) => {
  const now = new Date();
  return Array.from({ length: count }, (_, index) => {
    const date = new Date(
      Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - index, 1),
    );
    return date.toISOString().slice(0, 10);
  });
};

const MonthLabel = ({ month }: { month: string }) => {
  const [locale] = useLocaleState();
  const label = new Date(`${month}T00:00:00Z`).toLocaleDateString(locale, {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
  return <>{label.charAt(0).toUpperCase() + label.slice(1)}</>;
};

export const SideCard = ({
  side,
  name,
  canGrant,
  onGranted,
}: {
  side: CreditSide;
  name: string;
  canGrant: boolean;
  onGranted: () => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const value = Number.parseInt(amount, 10);

  const grant = async () => {
    setBusy(true);
    const { error } = await getSupabaseClient().rpc("grant_credits", {
      sale: side.id,
      amount: value,
      note: note.trim(),
    });
    setBusy(false);
    setConfirming(false);
    if (error) {
      notify(error.message, { type: "error" });
      return;
    }
    notify("resources.projects.credits.granted", { type: "info" });
    setAmount("");
    setNote("");
    onGranted();
  };

  return (
    <Card>
      <CardContent className="flex flex-wrap items-center justify-between gap-3 text-sm">
        <div>
          <div className="font-medium">{name}</div>
          <div className="text-xs text-muted-foreground">
            {translate("resources.projects.credits.members", {
              smart_count: side.members,
            })}
          </div>
        </div>
        <div className="text-right">
          <div className="text-lg font-semibold">
            {translate("resources.projects.credits.balance", {
              smart_count: side.balance,
            })}
          </div>
          {side.monthly_credits > 0 && (
            <div className="text-xs text-muted-foreground">
              {translate("resources.projects.credits.monthly_side", {
                monthly: side.monthly_credits,
              })}
            </div>
          )}
          {side.reserved > 0 && (
            <div className="text-xs text-muted-foreground">
              {translate("resources.projects.credits.reserved", {
                reserved: side.reserved,
              })}
            </div>
          )}
        </div>
        <p className="basis-full text-xs text-muted-foreground">
          {translate("resources.projects.credits.pool_info")}
        </p>
        {canGrant && (
          <div className="flex items-center gap-2">
            <Input
              type="number"
              className="w-24"
              value={amount}
              placeholder={translate("resources.projects.credits.amount")}
              aria-label={translate("resources.projects.credits.amount")}
              onChange={(event) => setAmount(event.target.value)}
            />
            {/* Motif obligatoire : il reste dans le journal des mouvements. */}
            <Input
              className="w-48"
              value={note}
              placeholder={translate("resources.projects.credits.note")}
              aria-label={translate("resources.projects.credits.note")}
              onChange={(event) => setNote(event.target.value)}
            />
            <Button
              size="sm"
              variant="outline"
              disabled={busy || !value || !note.trim()}
              onClick={() => setConfirming(true)}
            >
              {translate(
                `resources.projects.credits.${value < 0 ? "remove" : "grant"}`,
              )}
            </Button>
            <Confirm
              isOpen={confirming}
              loading={busy}
              title={translate(
                `resources.projects.credits.${value < 0 ? "confirm_remove" : "confirm_title"}`,
                { smart_count: Math.abs(value), name },
              )}
              content={note.trim()}
              confirm={translate(
                `resources.projects.credits.${value < 0 ? "remove" : "grant"}`,
              )}
              onConfirm={grant}
              onClose={() => setConfirming(false)}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

/** Consommation du mois par personne ; sur téléphone, une carte par personne. */
const UsageTable = ({
  rows,
  selected,
  onSelect,
}: {
  rows: CreditUsage[];
  selected: number | null;
  onSelect: (id: number) => void;
}) => {
  const translate = useTranslate();
  const total = rows.reduce((sum, row) => sum + row.spent, 0);
  if (rows.every((row) => row.spent === 0)) {
    return (
      <p className="text-sm text-muted-foreground">
        {translate("resources.projects.credits.no_usage")}
      </p>
    );
  }
  const share = (row: CreditUsage) =>
    total > 0 ? `${Math.round((row.spent * 100) / total)} %` : "–";
  const label = (key: string) =>
    translate(`resources.projects.credits.columns.${key}`);
  return (
    <ul className="flex flex-col divide-y text-sm">
      <li className="hidden sm:grid grid-cols-[1fr_repeat(5,5.5rem)] gap-2 pb-2 text-xs text-muted-foreground">
        <span>{label("person")}</span>
        <span className="text-right">{label("emails")}</span>
        <span className="text-right">{label("mobiles")}</span>
        <span className="text-right">{label("teams")}</span>
        <span className="text-right">{label("spent")}</span>
        <span className="text-right">{label("share")}</span>
      </li>
      {rows.map((row) => (
        <li key={row.sales_id}>
          <button
            type="button"
            onClick={() => onSelect(row.sales_id)}
            className={`w-full text-left py-2 grid grid-cols-2 sm:grid-cols-[1fr_repeat(5,5.5rem)] gap-x-2 gap-y-1 hover:bg-muted/50 rounded ${selected === row.sales_id ? "bg-muted" : ""}`}
          >
            <span className="col-span-2 sm:col-span-1 font-medium">
              {row.first_name} {row.last_name}
              {row.disabled && (
                <span className="ml-1 text-xs text-muted-foreground">
                  ({translate("resources.projects.credits.disabled")})
                </span>
              )}
            </span>
            <UsageCell label={label("emails")} value={row.emails} />
            <UsageCell label={label("mobiles")} value={row.mobiles} />
            <UsageCell label={label("teams")} value={row.teams} />
            <UsageCell label={label("spent")} value={row.spent} strong />
            <UsageCell label={label("share")} value={share(row)} />
          </button>
        </li>
      ))}
      {rows.length > 1 && (
        <li className="py-2 grid grid-cols-2 sm:grid-cols-[1fr_repeat(5,5.5rem)] gap-2 font-medium">
          <span>{label("total")}</span>
          <span className="hidden sm:block" />
          <span className="hidden sm:block" />
          <span className="hidden sm:block" />
          <span className="text-right">{total}</span>
          <span className="hidden sm:block" />
        </li>
      )}
    </ul>
  );
};

const UsageCell = ({
  label,
  value,
  strong,
}: {
  label: string;
  value: number | string;
  strong?: boolean;
}) => (
  <span
    className={`flex justify-between sm:block sm:text-right ${strong ? "font-semibold" : ""}`}
  >
    <span className="sm:hidden text-xs text-muted-foreground">{label}</span>
    {value}
  </span>
);

const MovementRow = ({
  movement,
  name,
}: {
  movement: CreditMovement;
  name?: string;
}) => {
  const translate = useTranslate();
  const [locale] = useLocaleState();
  const reason = translate(
    `resources.projects.credits.reasons.${movement.reason}`,
  );
  return (
    <li className="flex flex-wrap justify-between gap-2 text-sm">
      <div>
        <span className="text-muted-foreground">
          {formatDay(movement.created_at, locale)} ·{" "}
        </span>
        {name ? `${name} · ${reason}` : reason}
        {movement.project_id && (
          <>
            {" · "}
            <Link
              to={`/projects/${movement.project_id}/show`}
              className="underline"
            >
              {translate("resources.projects.credits.project")}
            </Link>
          </>
        )}
        {movement.note && ` · ${movement.note}`}
      </div>
      <div className={movement.delta < 0 ? "" : "font-medium"}>
        {movement.delta > 0 ? `+${movement.delta}` : movement.delta}
      </div>
    </li>
  );
};

/** Paramètres sur téléphone : le solde, et l'accès à l'écran des crédits. */
export const CreditsSection = () => {
  const translate = useTranslate();
  const { data: identity } = useGetIdentity();
  const { data: balances } = useGetList<CreditBalance>(
    "credit_balances",
    { filter: { id: identity?.id }, pagination: { page: 1, perPage: 1 } },
    { enabled: !!identity },
  );
  const own = balances?.[0];
  return (
    <div>
      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide px-1 mb-1.5">
        {translate("resources.projects.credits.title")}
      </p>
      <ItemGroup className="rounded-lg border overflow-hidden">
        <Item asChild size="sm" className="cursor-pointer">
          <Link to="/credit_balances">
            <ItemContent>
              <ItemTitle className="font-normal">
                {own
                  ? translate("resources.projects.credits.balance", {
                      smart_count: own.balance,
                    })
                  : translate("resources.projects.credits.title")}
              </ItemTitle>
            </ItemContent>
            <ItemActions>
              <ChevronRight className="size-4 text-muted-foreground" />
            </ItemActions>
          </Link>
        </Item>
      </ItemGroup>
    </div>
  );
};
